import axios from 'axios';
import { createSlice } from '@reduxjs/toolkit';
import url from '../../constants/config';
import { toasterOn } from '../../shared/store/actions';

const getAuthHeader = () => ({
  Authorization: `Bearer ${window.localStorage.getItem('authorization')}`,
});

export const adminConversationsSlice = createSlice({
  name: 'adminConversations',
  initialState: {
    list: [],
    activeThread: null,
    messages: [],
    nextCursor: null,
    adminUnreadCount: 0,
    loading: false,
    coaches: [],
    assistants: [],
    players: [],
  },
  reducers: {
    setConversations: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.list = action.payload;
      // eslint-disable-next-line no-param-reassign
      state.adminUnreadCount = action.payload.reduce(
        (sum, c) =>
          sum +
          (c.unreadCount || 0) +
          (c.markedUnreadByAdmin && !(c.unreadCount > 0) ? 1 : 0),
        0
      );
    },
    setActiveThread: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.activeThread = action.payload.conversation;
      // eslint-disable-next-line no-param-reassign
      state.messages = action.payload.messages;
      // eslint-disable-next-line no-param-reassign
      state.nextCursor = action.payload.nextCursor;
    },
    prependMessages: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.messages = [...action.payload.messages, ...state.messages];
      // eslint-disable-next-line no-param-reassign
      state.nextCursor = action.payload.nextCursor;
    },
    appendMessage: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.messages = [...state.messages, action.payload];
    },
    setLoading: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.loading = action.payload;
    },
    clearThread: state => {
      // eslint-disable-next-line no-param-reassign
      state.activeThread = null;
      // eslint-disable-next-line no-param-reassign
      state.messages = [];
      // eslint-disable-next-line no-param-reassign
      state.nextCursor = null;
    },
    setAdminUnreadCount: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.adminUnreadCount = action.payload;
    },
    setCoaches: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.coaches = action.payload;
    },
    setAssistants: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.assistants = action.payload;
    },
    setPlayers: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.players = action.payload;
    },
  },
});

export const {
  setConversations,
  setActiveThread,
  prependMessages,
  appendMessage,
  setLoading,
  clearThread,
  setAdminUnreadCount,
  setCoaches,
  setAssistants,
  setPlayers,
} = adminConversationsSlice.actions;

export const fetchAdminConversations = () => async dispatch => {
  try {
    dispatch(setLoading(true));
    const res = await axios.get(`${url}/admin-conversations`, { headers: getAuthHeader() });
    dispatch(setConversations(res.data));
  } catch (err) {
    dispatch(toasterOn({ message: 'Failed to load conversations', type: 'failed' }));
  } finally {
    dispatch(setLoading(false));
  }
};

export const fetchAdminThread = id => async dispatch => {
  try {
    dispatch(setLoading(true));
    const res = await axios.get(`${url}/admin-conversations/${id}`, { headers: getAuthHeader() });
    dispatch(setActiveThread(res.data));
  } catch (err) {
    dispatch(toasterOn({ message: 'Failed to load conversation', type: 'failed' }));
  } finally {
    dispatch(setLoading(false));
  }
};

export const fetchAdminMoreMessages = (id, cursor) => async dispatch => {
  try {
    const res = await axios.get(`${url}/admin-conversations/${id}/messages?before=${cursor}`, {
      headers: getAuthHeader(),
    });
    dispatch(prependMessages(res.data));
  } catch (err) {
    dispatch(toasterOn({ message: 'Failed to load older messages', type: 'failed' }));
  }
};

export const createAdminConversation = ({ coachUserId, text, file }) => async dispatch => {
  try {
    dispatch(setLoading(true));
    const headers = getAuthHeader();
    let res;
    if (file) {
      const formData = new FormData();
      formData.append('coachUserId', coachUserId);
      if (text) formData.append('text', text);
      formData.append('attachment', file);
      res = await axios.post(`${url}/admin-conversations`, formData, { headers });
    } else {
      res = await axios.post(
        `${url}/admin-conversations`,
        { coachUserId, text },
        { headers }
      );
    }
    dispatch(toasterOn({ message: 'Wiadomość wysłana!', type: 'success' }));
    return res.data;
  } catch (err) {
    dispatch(toasterOn({ message: 'Nie udało się wysłać wiadomości', type: 'failed' }));
    return null;
  } finally {
    dispatch(setLoading(false));
  }
};

export const sendAdminReply = ({ conversationId, text, file, draftRevision }) => async dispatch => {
  try {
    let response;
    const headers = getAuthHeader();
    if (file) {
      const formData = new FormData();
      if (draftRevision !== undefined) formData.append('draftRevision', String(draftRevision));
      if (text) formData.append('text', text);
      formData.append('attachment', file);
      response = await axios.post(
        `${url}/admin-conversations/${conversationId}/messages`,
        formData,
        { headers }
      );
    } else {
      response = await axios.post(
        `${url}/admin-conversations/${conversationId}/messages`,
        { text, draftRevision },
        { headers }
      );
    }
    dispatch(appendMessage(response.data.message));
    return response.data.message;
  } catch (err) {
    dispatch(toasterOn({ message: 'Failed to send reply', type: 'failed' }));
    return null;
  }
};

export const fetchAdminUnreadCount = () => async dispatch => {
  try {
    const res = await axios.get(`${url}/admin-conversations/unread-count`, {
      headers: getAuthHeader(),
    });
    dispatch(setAdminUnreadCount(res.data.count));
  } catch (err) {
    // silently fail — non-critical background poll
  }
};

export const markAdminThreadRead = conversationId => async dispatch => {
  try {
    await axios.patch(
      `${url}/admin-conversations/${conversationId}/read`,
      {},
      { headers: getAuthHeader() }
    );
  } catch (err) {
    // silently fail
  }
};

export const markAdminConversationUnread = conversationId => async dispatch => {
  try {
    await axios.patch(
      `${url}/admin-conversations/${conversationId}/mark-unread`,
      {},
      { headers: getAuthHeader() }
    );
  } catch (err) {
    // silently fail
  }
};

export const fetchCoaches = () => async dispatch => {
  try {
    const res = await axios.get(`${url}/admin-conversations/coaches`, {
      headers: getAuthHeader(),
    });
    dispatch(setCoaches(res.data));
  } catch (err) {
    dispatch(toasterOn({ message: 'Failed to load coaches', type: 'failed' }));
  }
};

export const fetchAssistants = () => async dispatch => {
  try {
    const res = await axios.get(`${url}/admin-conversations/assistants`, {
      headers: getAuthHeader(),
    });
    dispatch(setAssistants(res.data));
  } catch (err) {
    dispatch(toasterOn({ message: 'Failed to load assistants', type: 'failed' }));
  }
};

export const fetchPickerPlayers = () => async dispatch => {
  try {
    const res = await axios.get(`${url}/admin-conversations/players`, {
      headers: getAuthHeader(),
    });
    dispatch(setPlayers(res.data));
  } catch (err) {
    dispatch(toasterOn({ message: 'Failed to load players', type: 'failed' }));
  }
};

export default adminConversationsSlice.reducer;
